import React, {useState, useEffect, useRef} from 'react'
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from 'react-hook-form'
import { Box, Stack, Tabs, Tab, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled, useTheme } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { GridToolbarColumnsButton } from '@mui/x-data-grid';
import { GridToolbarFilterButton } from '@mui/x-data-grid';
import { GridToolbarDensitySelector } from '@mui/x-data-grid';
import { GridToolbarExport } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import dayjs from "dayjs";
import 'dayjs/locale/ja'
import { userURL, apiURL } from './App';
import { authJson } from './apiClient';
import { PrimaryButton, SecondButton } from './Controls';
import { ThemeInputText } from './Controls';
import { SelectDate, SelectDateRange } from './SelectDate';
import { CheckSelect } from './CheckSelect';
import { SingleSelect } from './Controls';
import { toDateString } from './Controls';

// 検索リストツールバー
const SearchListToolbar = () => {
  let timestamp = dayjs().format('YYYYMMDDHHmmss')
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport csvOptions={{ fileName: `interviews_${timestamp}.csv`, utf8WithBom: true }}/>
    </GridToolbarContainer>
  );
}

// 共通コンテナ
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: '#fff',
  position: 'absolute',
  padding: '20px',
  width: '100%',
  height: '100%'
});

// 共通カード
export const Card = styled('div')({
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
  padding: '20px',
  margin: '20px 20px 0px 20px',
  width: '100%',
});

// タブパネル
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="panel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && 
        <Container>{children}</Container>}
    </div>
  );
}

// データグリッドのテーマカスタマイズ
export const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
  },
  // 列メニュー（…）を完全に非表示
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  // ヘッダ列のフォーカス枠を非表示
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
    boxShadow: 'none',
  },
  '& .MuiDataGrid-filler': {
    backgroundColor: theme.palette.secondary.light,
  },
  '& .MuiDataGrid-root': {
    backgroundColor: theme.palette.secondary.light,
  },
  // セル選択枠（フォーカスアウトライン）を非表示
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
}));

// タブ属性
function tabProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

// 取材検索ページ
const SearchPage = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { control, register, handleSubmit, setValue, getValues, reset } = useForm();
  const [ condition, setCondition ] = useState(null);
  const [ period, setPeriod ] = useState([]);
  const [ programs, setPrograms ] = useState([]);
  const [ users, setUsers ] = useState([]);
  const [ areas, setAreas ] = useState([]);
  const [ genres, setGenres ] = useState([]);
  const [ allrows, setAllRows ] = useState([]);
  const [ rows, setRows ] = useState([]);
  const [ processing, setProcessing ] = useState(false);
  const [ formReset, setFormReset ] = useState(false);
  const anchorRef = useRef(null);

  // 取材リスト
  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 50
    },
    {
      field: 'interview_date',
      headerName: '取材日',
      width: 120,
    },
    {
      field: 'interview_name',
      headerName: '取材名',
      width: 150,
    },
    {
      field: 'program_names',
      headerName: '番組名',
      width: 150,
    },
    {
      field: 'ucv_person_names',
      headerName: '担当者',
      width: 150,
    },
    {
      field: 'interview_place',
      headerName: '取材場所',
      width: 150,
    },
    {
      field: 'interview_starttime',
      headerName: '開始時間',
      width: 100,
      renderCell: (params) => {
        // 秒は表示しない
        const v = params.value;
        if (!v) return '';
        return v.toString().slice(0,5);
      },
    },
    {
      field: 'interview_endtime',
      headerName: '終了時間',
      width: 100,
      renderCell: (params) => {
        // 秒は表示しない
        const v = params.value;
        if (!v) return '';
        return v.toString().slice(0,5);
      },
    },
    {
      field: 'entry_account_name',
      headerName: '登録者',
      width: 120,
    },
    {
      field: 'is_files',
      headerName: '添付ファイル',
      width: 110,
      renderCell: (params) => {
        const files = params.row.files;
        return files && Array.isArray(files) && files.length > 0 ? 'あり' : 'なし';
      },
    },
    {
      field: 'program_date',
      headerName: '放送日',
      width: 120,
      renderCell: (params) => {
        const v = params.value;
        return v === null || v === undefined || v === '' ? '未設定' : v;
      },
    },
  ];

  // 初期表示
  useEffect(() => {
    // 並列取得
    void Promise.all([
      // ユーザリスト取得
      fetchUserList(),
      // 番組リスト取得
      fetchProgramList(),
      // エリアリスト取得
      fetchAreaList(),
      // ジャンルリスト取得
      fetchGenreList(),
    ]);
    // 検索条件初期化
    let form = {}    
    // 検索条件取得
    const json = sessionStorage.getItem("condition")
    if (json) { 
      // Jsonから連想配列へ変換
      form = JSON.parse(json)
    }
    // 検索条件項目数分
    for (let name in form) {
      // フォーム値へ設定
      setValue(name, form[name]);
    }
    // 検索条件設定
    setCondition(form)

    // 表示期間取得
    const str = sessionStorage.getItem("period")
    if (str) {
      // カンマセパレータから日付型配列へ変換
      const dates = str.split(",").map(date => date ? new Date(date) : null)
      // 表示期間設定
      setPeriod(dates)
    }
  }, [])

  // 検索条件変更
  useEffect(() => {
    // 検索条件保存（Json）
    if (condition) sessionStorage.setItem("condition", JSON.stringify(condition))
    // 取材データリスト取得
    fetchRows()
  }, [condition])

  // 表示期間変更
  useEffect(() => {
    // 表示期間保存（カンマセパレータ）
    if (period) sessionStorage.setItem("period", period.toString())
    // 取材データリスト絞り込み
    filterRows()
  }, [period])

  // 検索条件リセット
  useEffect(() => {
    // リセット要求
    if (formReset) {
      // フォームのリセット（keywordsも空文字で明示的にクリア）
      reset({ keywords: "" })
      // リセット済み
      setFormReset(false)
    }
  }, [formReset])

  // ユーザリスト取得
  const fetchUserList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${userURL}/fullnames/`);
      // ユーザリスト設定
      setUsers(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ユーザリスト初期化
      setUsers([]);
    }
  }

  // 番組リスト取得
  const fetchProgramList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/master/programs`);
      // 番組リスト設定
      setPrograms(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // 番組リスト初期化
      setPrograms([]);
    }
  }

  // エリアリスト取得
  const fetchAreaList = async () => {
    try {
    // API呼び出し
      const data = await authJson(`${apiURL}/master/areas`);
      // エリアリスト設定
      setAreas(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // エリアリスト初期化
      setAreas([]);
    }
  }

  // ジャンルリスト取得
  const fetchGenreList = async () => {
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/master/genres`);
      // ジャンルリスト設定
      setGenres(data);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // ジャンルリスト初期化
      setGenres([]);
    }
  }

  // 取材データリスト取得
  const fetchRows = async () => {
    // 検索条件が指定されていない
    if (condition === null) {
      return;
    }
    // キーワードの配列化
    if (condition.keywords && !Array.isArray(condition.keywords)) {
      condition.keywords = condition.keywords.split(/[\s]+/).filter(Boolean);
    }
    // フォームデータ追加
    const body = new FormData()
    body.append("props", JSON.stringify(condition))
    console.log(...body.entries());
    // 取材データ検索
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/interviews`, { method: 'POST', body: body, actionName: 'SearchInterviews' });
      // 全行保持
      setAllRows(data);
      // 表示期間で絞り込み
      const filterRows = searchPeriod(data, period);
      // 表示行更新
      setRows(filterRows);
    }
    catch (e) {
      // 取得エラー
      console.log(e);
      // 全行初期化
      setAllRows([]);
      // 表示行初期化
      setRows([]);
    }
  }

  // 取材データリスト絞り込み
  const filterRows = () => {
    // 取材データを表示期間で絞り込み
    const filterRows = searchPeriod(allrows, period)
    // 表示取材データリスト設定
    setRows(filterRows)
  }

  // 取材データリスト表示期間検索
  const searchPeriod = (source, dates) => {
    // 表示期間取得
    let [start, end] = dates;
    if (end == null) {
      end = start
    }
    // 表示期間が指定されている
    if (start != null) {
      // 取材データ一覧から取材日を検索
      var result = source.filter(function(row) {
        if (row.interview_date != "") {
          let date = dayjs(row.interview_date);
          if (start <= date && end >= date) {
            return true;
          }
        }
        return false;
      });
      // 表示期間の取材データを返す
      return result;
    }
    // 取材データ全件を返す
    return source;
  }

  // タブ変更
  const handleTabChange = (event, newValue) => {
    if (newValue == 1) {
      // 地図へ
      navigate("view/map", { state: { period: period, rows: allrows } });
    }
    else if (newValue == 2) {
      // カレンダーへ
      navigate("view/calender", { state: { period: period, rows: allrows } });
    }
  };

  // クリアボタン
  const onFormClear = (e) => {
    // 検索条件リセット
    setFormReset(true)
  };

  // CSV出力
  const onOutputCsv = (e) => {
      const link = anchorRef.current
      if (!link) return

      // ヘッダ生成
      const headers = columns.map((col) => {
        if (col.headerName !== "") {
          return (col.headerName)
        }
      }).filter((header) => header != undefined)

      // データキーリスト生成
      const keys = columns.map((col) => {
        if (col.headerName !== "") {
          return (col.field)
        }
      }).filter((key) => key != undefined)

      // データ値リスト生成
      const values = rows.map((row) => {
        return (
          keys.map((key) => {
            let value = ""
            if (row[key] != null) {
              value = `"${row[key].toString()}"`
            }
            return (value)
          })
        )
      })

      // CSV文字列作成
      const table = [headers].concat(values)
      const csvText = table.map((str) => str.join(',')).join('\n')
      console.log(csvText)
    
      // CSVファイル名作成
      let timestamp = dayjs().format('YYYYMMDDHHmmss')
      const filename = `interviews_${timestamp}.csv`

      // CSVファイル出力
      const bom  = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, csvText], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.click()
  };

  // 取材検索
  const searchData = async (form) => {
    // 日付フォーマット
    form.interview_date = toDateString(form.interview_date);
    form.program_date = toDateString(form.program_date);
    // キーワードの全角スペースを半角スペースへ全置換
    if (form.keywords && typeof form.keywords === 'string') {
      form.keywords = form.keywords.replace(/\u3000/g, ' ');
    }
    // 検索条件項目数分
    for (let name in form) {
      // 空文字、null、空配列はundefinedへ変換
      if (form[name] === '' || form[name] === null ||
         (Array.isArray(form[name]) && form[name].length == 0)) {
        form[name] = undefined;
      }
      // フォーム値へ設定
      setValue(name, form[name]);
    }
    // 検索条件設定
    setCondition(form)
  }

  // 表示期間
  const onChangePeriod = (dates) => {
    // 表示期間設定
    setPeriod(dates);
  }

  // レンダリング
  return (
    <Container>
      <Card>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>取材検索</Typography>
          <Typography variant="inherit" color="text.disabled">条件を指定して検索</Typography>
        </Box>
        <Box component="form" id="searchForm" onSubmit={handleSubmit(searchData)} sx={{ mt: 3, width: "100%" }}>
          <Grid container spacing={2}>
            <Grid size={2.4}>
              <CheckSelect id="ucv_person_ids" label="担当者" placeholder="プルダウンから選択" value={getValues("ucv_person_ids")} options={users} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <CheckSelect id="entry_account_id" label="登録者" placeholder="プルダウンから選択" value={getValues("entry_account_id")} options={users} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <ThemeInputText id="keywords" label="キーワード(複数入力可能)" placeholder="フリーワードを入力" control={control}/>
            </Grid>
            <Grid size={2.4}>
              <SelectDate id="interview_date" label="取材日" placeholder="カレンダーから選択" control={control}/>
            </Grid>
            <Grid size={2.4}>
              <SingleSelect id="is_onair" label="放送予定" placeholder="プルダウンから選択" value={getValues("is_onair")} options={[{ id: '', name: '　' }, {id: true, name: 'あり'}, {id: false, name: 'なし'}]} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <SelectDate id="program_date" label="放送日" placeholder="カレンダーから選択" control={control}/>
            </Grid>
            <Grid size={2.4}>
              <CheckSelect id="program_ids" label="番組名" placeholder="プルダウンから選択" value={getValues("program_ids")} options={programs} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <CheckSelect id="genre_ids" label="ジャンル" placeholder="プルダウンから選択" value={getValues("genre_ids")} options={genres} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <CheckSelect id="area_ids" label="取材場所（エリア）" placeholder="プルダウンから選択" value={getValues("area_ids")} options={areas} register={register}/>
            </Grid>
            <Grid size={2.4}>
              <PrimaryButton variant="contained" sx={{mr: 1, minWidth: 100, width: "40%"}} onClick={onFormClear}>クリア</PrimaryButton>
              <SecondButton type="submit" form='searchForm' variant="contained" sx={{mr: 1, minWidth: 100, width: "40%"}}>検索</SecondButton>
            </Grid>
          </Grid>
        </Box>
      </Card>
      <Card>
        <Stack direction="column" spacing={2} sx={{ m: 1, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>取材一覧</Typography>
            <Typography variant="inherit" color="text.disabled">「取材検索」で条件を指定することで絞り込みが可能です</Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center">
            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={5} sx={{width: '100%'}}>
              <Typography variant="inherit" sx={{ fontWeight: 'normal' }}>表示方法</Typography>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: theme.palette.secondary.light }}>
                <Tabs value={0} onChange={handleTabChange}>
                  <Tab label="リスト" {...tabProps(0)} />
                  <Tab label="地図" {...tabProps(1)} />
                  <Tab label="カレンダー" {...tabProps(1)} />
                </Tabs>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ width: 300 }}>
                <SelectDateRange id="period" label="表示期間" placeholder="カレンダーから選択" onChange={onChangePeriod} value={period}/>
              </Box>
              <SecondButton variant="contained" onClick={onOutputCsv}>CSV出力</SecondButton>
              <a ref={anchorRef} className='hidden'></a>
            </Stack>
          </Box>
          <Box sx={{ pr: 2, width: '100%', height: '500px' }}>
            <ThemeDataGrid
              rows={rows}
              columns={columns}
              slots={{ toolbar: SearchListToolbar }}
              localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
              disableColumnMenu
              onRowClick={(params) => {
                // 詳細入力へ
                navigate(`details/${params.row.id}`, {state: {referrer: location.pathname}});
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Stack>
      </Card>
    </Container>
  );
};

export default SearchPage;